import axios from 'axios'
import router from '@/router'
import { clearAuth, getToken } from '@/utils/auth'

export const API_ORIGIN = process.env.NODE_ENV === 'development' ? 'https://zhyl-dx.zhiqiyf.com' : window.location.origin
const BASEURL = API_ORIGIN + '/api'

export function resolveAssetUrl(url) {
    if (!url) return ''
    if (/^https?:\/\//i.test(url)) return url
    if (url.indexOf('//') === 0) return 'https:' + url
    return API_ORIGIN + (url.indexOf('/') === 0 ? url : '/' + url)
}

// 私有附件由 img 标签加载时无法携带 Authorization 请求头，改用后端支持的 token 参数鉴权。
export function resolveAuthenticatedAssetUrl(url) {
    const assetUrl = resolveAssetUrl(url)
    const token = getToken()
    if (!assetUrl || !token || /[?&]token=/.test(assetUrl)) return assetUrl
    return assetUrl + (assetUrl.indexOf('?') === -1 ? '?' : '&') + 'token=' + encodeURIComponent(token)
}

function appendFormValue(params, key, value) {
    if (value === undefined || value === null) {
        return
    }

    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            appendFormValue(params, `${key}[${index}]`, item)
        })
        return
    }

    if (Object.prototype.toString.call(value) === '[object Object]') {
        Object.keys(value).forEach((childKey) => {
            appendFormValue(params, `${key}[${childKey}]`, value[childKey])
        })
        return
    }

    params.append(key, value)
}

function toFormBody(data) {
    const params = new URLSearchParams()
    Object.keys(data || {}).forEach((key) => {
        appendFormValue(params, key, data[key])
    })
    return params.toString()
}

function getResponseErrorMessage(error) {
    const response = error && error.response
    const data = response && response.data
    const errors = data && data.errors

    if (errors && typeof errors === 'object') {
        const fields = Object.keys(errors)
        for (let i = 0; i < fields.length; i += 1) {
            const messages = errors[fields[i]]
            if (Array.isArray(messages) && messages.length) return messages[0]
            if (typeof messages === 'string' && messages) return messages
        }
    }

    if (data && data.msg) return data.msg
    if (data && data.message && data.message !== 'The given data was invalid.') return data.message

    if (!response) return '网络连接失败，请检查网络后重试'
    if (response.status === 403) return '无权限执行该操作'
    if (response.status === 404) return '请求的数据不存在'
    if (response.status >= 500) return '服务器异常，请稍后重试'
    return '请求失败，请稍后重试'
}

export function handleLoginExpired(data) {
    clearAuth()
    import('@/store').then((module) => {
        module.default.dispatch('auth/clearUserInfo')
        module.default.dispatch('tagsView/resetVisitedTabs')
    })

    if (router.currentRoute.path !== '/login') {
        alertErr((data && data.msg) || '请重新登录')
        router.replace('/login')
    }
}

// 添加请求拦截器
axios.interceptors.request.use(
    function (config) {
        return config
    },
    function (error) {
        return Promise.reject(error)
    }
)

// 添加响应拦截器
axios.interceptors.response.use(
    function (res) {
        const data = res.data

        if (data && data.code === 402) {
            handleLoginExpired(data)
            return Promise.reject(data)
        }

        return data
    },
    function (error) {
        const response = error && error.response
        const data = response && response.data
        if ((data && data.code === 402) || (response && response.status === 402)) {
            handleLoginExpired(data)
            return Promise.reject(data || error)
        }
        alertErr(getResponseErrorMessage(error))
        return Promise.reject(error)
    }
)

export function api(action, method, data, options) {
    const reqUrl = BASEURL + action
    const reqMethod = method ? method.toLowerCase() : 'post'
    const skipAuth = options && options.skipAuth
    const token = getToken()
    const reqData = {
        user_id: localStorage.getItem('user_id') || '',
        ...data,
    }

    if (!skipAuth && token) {
        reqData.token = token
    }

    const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
    }

    if (!skipAuth && token) {
        headers.Authorization = 'Bearer ' + token
    }

    const config = {
        url: reqUrl,
        method: reqMethod,
        headers,
    }

    if (reqMethod === 'get') {
        config.params = reqData
    } else {
        config.data = toFormBody(reqData)
    }

    return axios(config)
}

/**
 * 上传外贸业务附件，统一走 /trade/files/upload。
 *
 * @param {File} file 要上传的文件
 * @param {Object} extra 可选业务参数：biz_module、biz_id、biz_type、remark
 * @param {Object} options 可选配置：skipAuth
 */
export function uploadFile(file, extra = {}, options = {}) {
    const formData = new FormData()
    formData.append('file', file)

    Object.keys(extra || {}).forEach((key) => {
        const value = extra[key]
        if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value)
        }
    })

    const skipAuth = options && options.skipAuth
    const token = getToken()
    const headers = {
        Accept: 'application/json',
    }

    if (!skipAuth && token) {
        headers.Authorization = 'Bearer ' + token
        formData.append('token', token)
    }

    return axios({
        url: BASEURL + '/trade/files/upload',
        method: 'post',
        data: formData,
        headers,
    })
}
